import { Button, Col, ConfigProvider, Popover, Row, Typography } from "antd";
import { uniqueId } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { BiChevronLeft } from "react-icons/bi";
import { INVENTORY_FIELD_ENUM, MAPPING_LABEL_TO_KEY, MAPPING_PATH_TO_VOCS_REPORT } from "src/entity/common/common.constant";
import { MONTHS_MAPPING } from "src/entity/common/time.constant";
import { ApprovalReportResponse, SubmitMonthlyResponse } from "src/entity/my-tasks/my-tasks";
import { profileStore } from "src/stores/profile-store";
import { componentPalette } from "src/theme/component-palette";
import { capitalizeFirstLetter } from "src/utils/manage-string";
import { useShallow } from "zustand/react/shallow";
const { Text, Title } = Typography;
export interface TaskItem {
  totalTasks: number;
  title: string;
  submitReport: SubmitMonthlyResponse;
  approvalReport: ApprovalReportResponse;
  isHovered: string | null;
  onHover: () => void;
  onLeave: () => void;
}

const myTaskTheme = {
  components: {
    Button: {
      colorPrimary: componentPalette.secondMyTask,
      colorPrimaryHover: componentPalette.mainMyTask,
    },
  },
};
// approve link
// https://scgc-smartvocs.scg.com/vocs-inventory/combustion/report-voc-emission?companyId=66b19ea78d5ec42ee6e870d9&year=2025&month=mar&approveNo=1
// approve link FUGITIVE
// https://scgc-smartvocs.scg.com/vocs-inventory/fugitive/report-voc-emission?companyId=66a32c0aa3d1da9e2e5efeaf&year=2024&approveNo=13
const TaskItem: React.FC<TaskItem> = ({ title, totalTasks, submitReport, approvalReport, isHovered, onHover, onLeave }) => {
  const [action, setAction] = useState<"submit-report" | "approval-report" | null>(null);

  const profile = profileStore(useShallow((state) => state.profile));
  useEffect(() => {
    if (profile?.activeCompany) {
      setAction(null);
    }
  }, [profile?.activeCompany]);

  const usingKey = MAPPING_LABEL_TO_KEY[title as keyof typeof MAPPING_LABEL_TO_KEY];
  // console.log("approvalReport :: ", approvalReport);
  // console.log("submitReport :: ", submitReport);

  const isDisabledSubmitReportBtn = useMemo(() => {
    if (submitReport && submitReport[usingKey]) {
      if (usingKey === INVENTORY_FIELD_ENUM.Fugitive) {
        return submitReport.fugitive.length === 0;
      } else {
        return Object.keys(submitReport[usingKey]).every((keyYear) => submitReport[usingKey][keyYear].length === 0);
      }
    } else return false;
  }, [usingKey, submitReport]);

  const isDisabledApprovalReportBtn = useMemo(() => {
    if (approvalReport && approvalReport[usingKey]) {
      if (approvalReport && approvalReport[usingKey]) {
        return Object.keys(approvalReport[usingKey]).every((keyYear) => approvalReport[usingKey][keyYear].length === 0);
      } else return false;
    } else return false;
  }, [usingKey, approvalReport]);

  const backBtn = (
    <Col className="cursor-pointer" onClick={() => setAction(null)} span={2}>
      <BiChevronLeft size={24} />
    </Col>
  );

  const goToReportVocs = (year: string) => {
    const newPath = MAPPING_PATH_TO_VOCS_REPORT[usingKey as keyof typeof MAPPING_PATH_TO_VOCS_REPORT];
    const fullUrl = `${window.location.origin}${newPath}?year=${year}`;
    window.open(fullUrl, "_blank");
  };
  const goToApproveReportVocs = (companyId: string, year: string, month: string, approveNo: string | number) => {
    const newPath = MAPPING_PATH_TO_VOCS_REPORT[usingKey as keyof typeof MAPPING_PATH_TO_VOCS_REPORT];
    // fugitive hasn't month
    const fullUrl = `${window.location.origin}${newPath}?companyId=${companyId}&year=${year}${month ? `&month=${month}` : ""}&approveNo=${approveNo}`;
    window.open(fullUrl, "_blank");
  };

  const renderSubmitReportMonthly = () => {
    return (
      <Row className="w-full max-w-[40rem] min-w-[20rem] overflow-y-auto">
        <Row className="bg-secondMyTask w-full p-2 rounded-tl-md rounded-tr-md">
          {backBtn}
          <Col span={22} className="text-center">
            <Text>Waiting for Submit</Text>
          </Col>
        </Row>
        <Row className="w-full">
          {usingKey === INVENTORY_FIELD_ENUM.Fugitive
            ? submitReport.fugitive.map((item, index) => {
                const numberRows = Math.ceil(submitReport.fugitive.length / 4);
                const isBottomLeftCornor = index >= numberRows * 4 - 4 && index % 4 === 0;

                const isBottomRightCornor = index >= numberRows * 4 - 1 && (index - 3) % 4 === 0;
                return (
                  <Col
                    key={`fugitive-${item.year}-${index}`}
                    className={`bg-white text-center p-1 border-b border-gray-200 border-r border-mainMyTask cursor-pointer hover:bg-secondMyTask ${isBottomLeftCornor ? "rounded-bl-md" : ""}
                    ${isBottomRightCornor ? "rounded-br-md" : ""}`}
                    onClick={() => goToReportVocs(item.year)}
                    span={6}
                  >
                    <Text>{item.year}</Text>
                  </Col>
                );
              })
            : Object.keys(submitReport[usingKey]).map((keyYear, i) => {
                const itemSubmit = submitReport[usingKey][keyYear];
                return (
                  <Row key={"root-submit-report-monthly" + uniqueId("submit-report-monthly") + i + `${keyYear}`} className="w-full">
                    {itemSubmit.map((item, index) => {
                      const numberRows = Math.ceil(itemSubmit.length / 4);
                      const isBottomLeftCornor =
                        (index === 0 || index % 4 === 0) &&
                        i === Object.keys(submitReport[usingKey]).length - 1 &&
                        index >= numberRows * 4 - 4;

                      // 3, 7, 11
                      const isBottomRightCornor =
                        (index === 3 || index === 7 || index === 11) &&
                        i === Object.keys(submitReport[usingKey]).length - 1 &&
                        index >= numberRows * 4 - 1;
                      return (
                        <Col
                          className={`hover:bg-secondMyTask bg-white p-1 border-r border-b ${index === 0 || index % 4 === 0 ? "border-l" : ""} 
                        ${index < 4 ? "border-t" : ""} border-mainMyTask text-center cursor-pointer ${isBottomLeftCornor ? "rounded-bl-md" : ""}
                        ${isBottomRightCornor ? "rounded-br-md" : ""}`}
                          key={uniqueId("submit-report-monthly-sub") + index + item.label + Math.random()}
                          span={6}
                          onClick={() => goToReportVocs(keyYear)}
                        >
                          <Text>{`${MONTHS_MAPPING[item.label as keyof typeof MONTHS_MAPPING]} ${keyYear}`}</Text>
                        </Col>
                      );
                    })}
                  </Row>
                );
              })}
        </Row>
      </Row>
    );
  };

  const renderApprovalReportMonthly = () => {
    return (
      <Row className="w-full max-w-[40rem] min-w-[20rem] overflow-y-auto">
        <Row className="bg-secondMyTask w-full p-2 rounded-tl-md rounded-tr-md">
          {backBtn}
          <Col span={22} className="text-center">
            <Text>Waiting for Approval</Text>
          </Col>
        </Row>
        <Row className="w-full">
          {usingKey === INVENTORY_FIELD_ENUM.Fugitive ? (
            <>
              {Object.keys(approvalReport[usingKey]).map((keyYear, i) => {
                return approvalReport[usingKey][keyYear].map((item, index) => {
                  const numberRows = Math.ceil(submitReport.fugitive.length / 4);
                  const isBottomLeftCornor =
                    (index === 0 || index % 4 === 0) &&
                    i === Object.keys(approvalReport[usingKey]).length - 1 &&
                    index >= numberRows * 4 - 4;
                  const isBottomRightCornor =
                    (index === 3 || index === 7 || index === 11) &&
                    i === Object.keys(approvalReport[usingKey]).length - 1 &&
                    index >= numberRows * 4 - 1;

                  return (
                    <Col
                      span={6}
                      className={`hover:bg-secondMyTask bg-white p-1 border-r border-b ${index === 0 || index % 4 === 0 ? "border-l" : ""} 
                    ${index < 4 ? "border-t" : ""} border-mainMyTask text-center cursor-pointer ${isBottomLeftCornor ? "rounded-bl-md" : ""}
                    ${isBottomRightCornor ? "rounded-br-md" : ""}`}
                      key={uniqueId("approve-report-monthly-sub") + `${index}` + `${item.month}` + `${Math.random()}` + `${item.approveNo}`}
                      onClick={() => goToApproveReportVocs(item.companyId, keyYear, item.month as string, item.approveNo)}
                    >
                      <Text>{`${item?.month ? capitalizeFirstLetter(item.month) : ""} ${keyYear}`}</Text>
                    </Col>
                  );
                });
              })}
            </>
          ) : (
            <>
              {Object.keys(approvalReport[usingKey]).map((keyYear, i) => {
                const approveReportKeyContainValue = Object.keys(approvalReport[usingKey]).some(
                  (key) => approvalReport[usingKey][key].length > 0,
                );
                return (
                  <Row
                    key={"root-approve-report-monthly" + uniqueId("approve-report-monthly") + i + keyYear}
                    className="flex flex-wrap w-full"
                  >
                    {approvalReport[usingKey][keyYear].map((item, index) => {
                      const numberRows = Math.ceil(approvalReport[usingKey][keyYear].length / 4);
                      const isBottomLeftCornor =
                        (index === 0 || index % 4 === 0) &&
                        i === Object.keys(approvalReport[usingKey]).length - 1 &&
                        index >= numberRows * 4 - 4;

                      // 3, 7, 11
                      const isBottomRightCornor =
                        (index === 3 || index === 7 || index === 11) &&
                        i === Object.keys(approvalReport[usingKey]).length - 1 &&
                        index >= numberRows * 4 - 1;
                      return (
                        <Col
                          span={6}
                          className={`hover:bg-secondMyTask bg-white p-1 border-r border-b ${index === 0 || index % 4 === 0 ? "border-l" : ""} 
                          ${index < 4 ? "border-t" : ""} border-mainMyTask text-center cursor-pointer ${isBottomLeftCornor ? "rounded-bl-md" : ""}
                          ${isBottomRightCornor ? "rounded-br-md" : ""}`}
                          key={
                            uniqueId("approve-report-monthly-sub") + `${index}` + `${item.month}` + `${Math.random()}` + `${item.approveNo}`
                          }
                          onClick={() => goToApproveReportVocs(item.companyId, keyYear, item.month as string, item.approveNo)}
                        >
                          <Text>{`${item?.month ? capitalizeFirstLetter(item.month) : ""} ${keyYear}`}</Text>
                        </Col>
                      );
                    })}
                  </Row>
                );
              })}
            </>
          )}
        </Row>
      </Row>
    );
  };
  const contentPopover = () => (
    <ConfigProvider theme={myTaskTheme}>
      {!action ? (
        <Row className="bg-transparent">
          <Col className="bg-transparent">
            <Button
              type={"primary"}
              style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
              // className="rounded-l-2xl"
              size={"large"}
              onClick={() => setAction("submit-report")}
              disabled={isDisabledSubmitReportBtn}
            >
              <Text>Waiting for Submit</Text>
            </Button>
          </Col>
          <Col>
            <Button
              type={"primary"}
              style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
              size={"large"}
              // className="rounded-r-2xl"
              onClick={() => setAction("approval-report")}
              disabled={isDisabledApprovalReportBtn}
            >
              <Text>Waiting for Approval</Text>
            </Button>
          </Col>
        </Row>
      ) : action === "submit-report" ? (
        renderSubmitReportMonthly()
      ) : (
        renderApprovalReportMonthly()
      )}
    </ConfigProvider>
  );
  return (
    <Popover placement="bottomLeft" title={""} content={contentPopover} trigger={["click", "hover"]}>
      <Row className="w-full cursor-pointer" onMouseEnter={onHover} onMouseLeave={onLeave}>
        <Col
          className={`rounded-l-2xl ${!isHovered ? "bg-mainMyTask" : isHovered && isHovered === title ? "bg-mainMyTask" : "bg-secondMyTask"}`}
          span={6}
        >
          <Col className="p-4 items-center text-center">
            <Title className="text-white" style={{ color: "white" }}>
              {totalTasks}
            </Title>
            <div className="-mt-5">
              <Text className="text-white">Tasks</Text>
            </div>
          </Col>
        </Col>
        <Col className="bg-white rounded-r-2xl" span={18}>
          <Col className="p-4 flex flex-col justify-center h-full">
            <Title level={title.length > 10 ? 4 : 2}>{title}</Title>
            <div className="-mt-2">
              <Text>There is task waiting for you.</Text>
            </div>
          </Col>
        </Col>
      </Row>
    </Popover>
  );
};

export default TaskItem;
